import React, { useState } from 'react';

/**
 * VERAX: ayuda en pantalla
 *
 * Cada pestaña abre con un recuadro corto que responde tres preguntas: para qué
 * sirve, cómo se lee y qué se hace. Se puede ocultar; la preferencia se guarda
 * en este navegador.
 */

interface HelpText { titulo: string; para: string; leer: string[]; hacer: string[] }

export const HELP: Record<string, HelpText> = {
  dashboard: {
    titulo: 'Resumen',
    para: 'Muestra en una sola vista cómo está hoy la protección de cada marca y de sus directivos.',
    leer: [
      'Cada tarjeta cuenta amenazas según su gravedad: rojo es crítica, naranja alta, amarillo media.',
      'Una amenaza crítica es un sitio que se hace pasar por la marca y pide datos o pagos.',
      'Mapa: país donde están alojados los sitios falsos. Rojo o ámbar son amenazas actuales; gris azulado, casos registrados de junio a septiembre.',
      'Abajo verá qué revisó el sistema en el último barrido y cuánto tardó.'
    ],
    hacer: ['Haga clic en una tarjeta para ver el detalle.', 'Atienda primero las críticas y las altas.', 'Por seguridad, la sesión se cierra tras 5 minutos sin actividad; un minuto antes aparece un aviso.']
  },
  historico: {
    titulo: 'Casos de junio a septiembre de 2026',
    para: 'Reúne los sitios que suplantaron a la empresa en esos cuatro meses: cuántos fueron, cuántos se dieron de baja y desde dónde operan.',
    leer: [
      'Barras: verde son sitios ya dados de baja; naranja, sitios que seguían en proceso al cerrar el mes.',
      'Exposición: días que pasaron entre el registro del dominio falso y su notificación. Menos es mejor.',
      'Mapa: país del servidor o del registrador del sitio falso. "Estimado" significa que no se pudo comprobar.',
      'Actores reincidentes: varios sitios que publican el mismo número de contacto.'
    ],
    hacer: ['Haga clic en un mes para filtrar el mapa y la tabla.', 'Use los actores reincidentes como soporte para la denuncia.']
  },
  verificacion: {
    titulo: 'Verificación de casos',
    para: 'Muestra lo que VERAX comprobó por sí mismo, con fecha, sobre cada dominio de junio a septiembre.',
    leer: [
      'En línea: el sitio falso todavía responde. Fuera de línea: ya no está disponible.',
      'Ventaja: cuántos días antes de la notificación lo habría detectado VERAX, contados desde su primer certificado de seguridad.',
      'Confirmado: el sitio se presenta como la marca y además tiene con qué defraudar (formulario, pago o WhatsApp).',
      'Para revisión: hay señales, pero una persona debe decidir.'
    ],
    hacer: ['Si hay solicitudes pendientes, use el botón superior para ir a Reportes y aprobarlas.']
  },
  analyze: {
    titulo: 'Analizar un enlace sospechoso',
    para: 'Sirve cuando alguien le reenvía un enlace y quiere saber si suplanta a la empresa.',
    leer: [
      'Puntaje de amenaza (0 a 100): qué tan peligroso es el dominio.',
      'Puntaje de suplantación (0 a 100): qué tanto se parece al sitio oficial y si pide datos.'
    ],
    hacer: ['Pegue el dominio o el enlace, elija la empresa y pulse Verificar.', 'Si el resultado es alto, quedará en Amenazas con su evidencia.']
  },
  threats: {
    titulo: 'Amenazas',
    para: 'Lista los sitios sospechosos que el sistema encontró, ordenados por gravedad.',
    leer: [
      '"Confirmado por VERAX": el sistema verificó que suplanta a la marca y que tiene con qué defraudar.',
      'Cada amenaza guarda capturas y su huella digital (SHA-256) como evidencia.'
    ],
    hacer: ['Abra una amenaza para ver la evidencia y por qué se confirmó.', 'Las confirmadas ya tienen su solicitud de baja preparada en Reportes.']
  },
  executives: {
    titulo: 'Identidad en redes',
    para: 'Busca cuentas falsas de la empresa y de sus directivos en redes sociales.',
    leer: [
      'El perfil oficial es la referencia: nunca se marca como sospechoso.',
      '"Pendiente de su revisión": la red social no permite revisión automática y una persona debe decidir.'
    ],
    hacer: ['Revise cada candidato y marque "Es suplantación" o "Descartar".', 'Para vigilar a un directivo debe registrar su autorización (Ley 1581).']
  },
  reports: {
    titulo: 'Reportes y constancias',
    para: 'Reúne las solicitudes de baja y los reportes a autoridades que el sistema dejó listos, y las constancias de vigilancia.',
    leer: [
      'Borrador: preparado por el sistema, todavía no se ha enviado.',
      'Aprobado, enviado, recibido y retirado: cada paso del proceso de baja.'
    ],
    hacer: ['Revise el borrador y apruébelo (solo un administrador puede).', 'Registre el envío, el número de radicado y la fecha de retiro.']
  },
  intel: {
    titulo: 'Inteligencia',
    para: 'Cada día el sistema revisa fuentes públicas de phishing y avisos oficiales, y propone reglas para detectar mejor.',
    leer: [
      'Regla propuesta: algo que el sistema aprendió, con la explicación y ejemplos.',
      'Ninguna regla cambia la detección hasta que un administrador la activa.'
    ],
    hacer: ['Lea la explicación de cada propuesta y pulse Activar o Descartar.']
  },
  admin: {
    titulo: 'Administración',
    para: 'Usuarios, correo de alertas y empresas vigiladas.',
    leer: [
      'Correo de alertas: servidor de envío, responsable de cada empresa y administrador general en copia.',
      'Registro de auditoría: quién hizo qué y cuándo. No se puede modificar.'
    ],
    hacer: ['Cree usuarios con el rol justo para su trabajo.', 'Use "Enviar prueba" para confirmar que los correos llegan.']
  }
};

const KEY = 'verax.ayuda.oculta';
const readHidden = (): Record<string, boolean> => {
  try { return JSON.parse(localStorage.getItem(KEY) || '{}'); } catch { return {}; }
};

export const PageHelp: React.FC<{ id: string }> = ({ id }) => {
  const h = HELP[id];
  const [hidden, setHidden] = useState<boolean>(() => Boolean(readHidden()[id]));
  if (!h) return null;
  const toggle = () => {
    const next = !hidden;
    setHidden(next);
    try { localStorage.setItem(KEY, JSON.stringify({ ...readHidden(), [id]: next })); } catch { /* sin almacenamiento: solo en esta sesión */ }
  };
  if (hidden) {
    return (
      <div className="mb-4 flex justify-end">
        <button onClick={toggle} className="text-xs text-gray-400 hover:text-gray-200 border border-gray-700 rounded-full px-3 py-1">
          ? Ayuda de esta pantalla
        </button>
      </div>
    );
  }
  return (
    <section aria-label={`Ayuda: ${h.titulo}`} className="mb-6 rounded-lg border border-gray-700 bg-gray-900/70 p-4">
      <div className="flex justify-between items-start gap-3">
        <div>
          <p className="text-xs uppercase tracking-wider text-cyan-400 mb-1">Cómo usar esta pantalla</p>
          <p className="text-sm text-gray-200">{h.para}</p>
        </div>
        <button onClick={toggle} className="text-xs text-gray-400 hover:text-gray-200 whitespace-nowrap">Ocultar ayuda</button>
      </div>
      <div className="grid md:grid-cols-2 gap-4 mt-3">
        <div>
          <p className="text-xs font-semibold text-gray-300 mb-1">Cómo leerla</p>
          <ul className="text-sm text-gray-400 space-y-1 list-disc ml-5">{h.leer.map((t) => <li key={t}>{t}</li>)}</ul>
        </div>
        <div>
          <p className="text-xs font-semibold text-gray-300 mb-1">Qué hacer</p>
          <ul className="text-sm text-gray-400 space-y-1 list-disc ml-5">{h.hacer.map((t) => <li key={t}>{t}</li>)}</ul>
        </div>
      </div>
    </section>
  );
};
